import type { GetListParams, Identifier } from "ra-core";
import { normalizePostgrestIlikeQuery } from "../commons/postgrestSearchQuery";

const CONTACT_SEARCH_COLUMNS = [
  "name",
  "full_name",
  "first_name",
  "last_name",
  "company_name",
  "email",
  "phone",
];

const looksLikeEmail = (value: string) => value.includes("@");

const digitsOnly = (value: string) => value.replace(/\D/g, "");

/**
 * Numbers are stored as typed ("(555) 012-4822"), so match the digits in
 * order with anything between them; a leading US "1" is optional.
 */
export const phoneDigitsPattern = (value: string) => {
  const digits = digitsOnly(value);
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return national.split("").join("%");
};

const looksLikePhone = (value: string) => {
  const digits = digitsOnly(value);
  return digits.length >= 7 && /^[\d\s+\-().]+$/.test(value);
};

/** Match "Jose Quezada" via first_name + last_name AND; emails/phones get dedicated paths. */
export const applyContactListSearch = (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  const trimmed = String(q).trim();
  if (!trimmed) {
    return { ...params, filter };
  }

  if (looksLikeEmail(trimmed)) {
    const searchTerm = normalizePostgrestIlikeQuery(trimmed);
    return {
      ...params,
      filter: {
        ...filter,
        "@or": {
          "email_fts@ilike": searchTerm,
          "name@ilike": searchTerm,
          "company_name@ilike": searchTerm,
        },
      },
    };
  }

  if (looksLikePhone(trimmed)) {
    return {
      ...params,
      filter: {
        ...filter,
        "phone_fts@ilike": phoneDigitsPattern(trimmed),
      },
    };
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return {
      ...params,
      filter: {
        ...filter,
        "first_name@ilike": normalizePostgrestIlikeQuery(words[0] ?? ""),
        "last_name@ilike": normalizePostgrestIlikeQuery(
          words.slice(1).join(" "),
        ),
      },
    };
  }

  return applyFullTextSearch(CONTACT_SEARCH_COLUMNS)(params);
};

const COMPANY_SEARCH_COLUMNS = [
  "name",
  "phone_number",
  "website",
  "zipcode",
  "city",
  "state_abbr",
  "primary_contact_first_name",
  "primary_contact_last_name",
  // Every contact of the account plus the company's own channels.
  "people_search",
];

export const applyCompanyListSearch = (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  const trimmed = String(q).trim();
  if (!trimmed) {
    return { ...params, filter };
  }

  if (looksLikeEmail(trimmed)) {
    const searchTerm = normalizePostgrestIlikeQuery(trimmed);
    return {
      ...params,
      filter: {
        ...filter,
        "@or": {
          "primary_contact_email_jsonb::text@ilike": searchTerm,
          "people_search@ilike": searchTerm,
          "name@ilike": searchTerm,
          "website@ilike": searchTerm,
        },
      },
    };
  }

  if (looksLikePhone(trimmed)) {
    return {
      ...params,
      filter: {
        ...filter,
        "@or": {
          "primary_contact_phone_jsonb::text@ilike":
            normalizePostgrestIlikeQuery(digitsOnly(trimmed)),
          "people_search@ilike": normalizePostgrestIlikeQuery(
            digitsOnly(trimmed),
          ),
        },
      },
    };
  }

  return applyFullTextSearch(COMPANY_SEARCH_COLUMNS, {
    useContactFtsColumns: false,
  })(params);
};

/** Search tickets by #id / digits, subject, or requester fields. */
export const applyFullTextSearch =
  (columns: string[], options: { useContactFtsColumns?: boolean } = {}) =>
  (params: GetListParams) => {
    if (!params.filter?.q) {
      return params;
    }
    const { useContactFtsColumns = true } = options;
    const { q, ...filter } = params.filter;
    const searchTerm = normalizePostgrestIlikeQuery(String(q));
    return {
      ...params,
      filter: {
        ...filter,
        "@or": columns.reduce((acc, column) => {
          if (useContactFtsColumns && column === "email")
            return {
              ...acc,
              [`email_fts@ilike`]: searchTerm,
            };
          if (useContactFtsColumns && column === "phone")
            return {
              ...acc,
              [`phone_fts@ilike`]: searchTerm,
            };
          else
            return {
              ...acc,
              [`${column}@ilike`]: searchTerm,
            };
        }, {}),
      },
    };
  };

/** Lists searched on the server by their own columns or by client name. */
export const CLIENT_RECORD_SEARCH: Record<
  string,
  { textColumns: string[]; amountColumn?: string; idColumn?: string }
> = {
  client_invoices: {
    textColumns: ["invoice_number", "description"],
    amountColumn: "amount",
  },
  client_subscriptions: {
    textColumns: ["name", "subscription_number", "reference_number"],
    amountColumn: "amount",
  },
  tickets: {
    textColumns: ["subject", "requester_name", "requester_email"],
    idColumn: "id",
  },
};

/**
 * Search runs on the server (every page, not just the loaded one): the
 * record's own columns, an amount or #id, or the client — by the companies
 * and people whose name, email or phone matched.
 */
export const buildClientRecordSearchOr = (
  query: string,
  config: { textColumns: string[]; amountColumn?: string; idColumn?: string },
  matches: { companyIds: Identifier[]; contactIds: Identifier[] },
): Record<string, string> => {
  const term = normalizePostgrestIlikeQuery(query);
  const or: Record<string, string> = {};
  for (const column of config.textColumns) or[`${column}@ilike`] = term;
  if (matches.companyIds.length) {
    or["company_id@in"] = `(${matches.companyIds.join(",")})`;
  }
  if (matches.contactIds.length) {
    or["contact_id@in"] = `(${matches.contactIds.join(",")})`;
  }
  const amountText = query.replace(/[$,\s]/g, "");
  if (config.amountColumn && /^\d+(\.\d{1,2})?$/.test(amountText)) {
    or[`${config.amountColumn}@eq`] = String(Number(amountText));
  }
  const idMatch = query.trim().match(/^#?(\d+)$/);
  if (config.idColumn && idMatch) {
    or[`${config.idColumn}@eq`] = idMatch[1];
  }
  return or;
};
